/**
 * Mounts the `AiProvider` the assistant UI talks to. The mode is explicit:
 * `preview` mounts the fixture provider and the screen must keep its
 * «نمایشی» notice; `live` mounts the HTTP provider and never substitutes
 * fixture data when a call fails. The provider is disposed (in-flight
 * requests abandoned) when the mode or tenant changes and on unmount, so a
 * tenant switch or logout cannot leak earlier requests into the next state.
 */
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { selectProvider, type AiProviderMode } from './select.ts';
import type { AiProvider } from './provider.ts';

const AiProviderContext = createContext<AiProvider | null>(null);

export function AiConnectProvider({
  config,
  provider,
  children,
}: {
  readonly config: AiProviderMode;
  /** Inject a ready provider (tests). */
  readonly provider?: AiProvider;
  readonly children: ReactNode;
}) {
  const key = config.mode === 'live' ? `live:${config.slug}` : 'preview';
  // The mode/tenant key, not the config object identity, decides when to rebuild.
  const value = useMemo(() => provider ?? selectProvider(config), [provider, key]);
  useEffect(() => () => value.dispose(), [value]);
  return <AiProviderContext.Provider value={value}>{children}</AiProviderContext.Provider>;
}

export function useAiProvider(): AiProvider {
  const value = useContext(AiProviderContext);
  if (!value) throw new Error('useAiProvider must be used inside <AiConnectProvider>');
  return value;
}
